import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Carousel, CarouselContent, CarouselItem } from "../ui/carousel";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { cloneBim3DPricingRules } from "../../lib/bim3dPricingDefaults";
import { HelpCircle } from "lucide-react";
import { getApiBase } from "../../lib/apiBase";

const groups = [
  { key: "generalSettings", title: "Paramètres généraux" },
  { key: "lodFactors", title: "Facteurs LOD" },
  { key: "loiFactors", title: "Facteurs LOI" },
  { key: "disciplineCoefficients", title: "Disciplines bâtiment" },
  { key: "infrastructureCoefficients", title: "Infrastructures" },
];

const toNumber = (value) => (value === "" ? "" : Number(value));
const getTierRate = (days, tiers) => {
  const value = Number(days) || 0;
  for (const tier of tiers || []) {
    if (value >= tier.min) return tier.rate;
  }
  return 0;
};

export default function AdminBim3DPage() {
  const [mediaItems, setMediaItems] = useState([]);
  const [rules, setRules] = useState(() => cloneBim3DPricingRules());
  const [savedRules, setSavedRules] = useState(() => cloneBim3DPricingRules());
  const [updatedAt, setUpdatedAt] = useState("");
  const [mediaCarouselApi, setMediaCarouselApi] = useState();
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const mediaInputRef = useRef(null);

  const fetchMedia = async () => {
    const API = getApiBase();
    const { data } = await axios.get(`${API}/api/admin/bim-3d/media`, { withCredentials: true });
    setMediaItems(data);
  };

  const fetchRules = async () => {
    const API = getApiBase();
    const { data } = await axios.get(`${API}/api/admin/bim-3d/pricing-rules`, { withCredentials: true });
    setRules(data);
    setSavedRules(data);
    setUpdatedAt(data.updatedAt || "");
  };

  useEffect(() => {
    fetchMedia().catch(() => toast.error("Impossible de charger les médias Hero."));
    fetchRules().catch(() => toast.error("Impossible de charger les règles de calcul."));
  }, []);

  const mediaPreview = useMemo(() => mediaItems.map((item) => ({ ...item, previewUrl: `${getApiBase()}${item.url}` })), [mediaItems]);

  useEffect(() => {
    if (!mediaCarouselApi) return undefined;

    const onSelect = () => setActiveMediaIndex(mediaCarouselApi.selectedScrollSnap());
    onSelect();
    mediaCarouselApi.on("select", onSelect);

    return () => {
      mediaCarouselApi.off("select", onSelect);
    };
  }, [mediaCarouselApi]);

  const handleUpload = async (files) => {
    if (!files.length) {
      toast.error("Ajoutez au moins un média avant l’envoi.");
      return;
    }

    try {
      const API = getApiBase();
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", file.name.replace(/\.[^.]+$/, ""));
        formData.append("caption", "");
        formData.append("isActive", "true");
        await axios.post(`${API}/api/admin/bim-3d/media`, formData, { withCredentials: true });
      }
      toast.success("Média(s) Hero importé(s).");
      if (mediaInputRef.current) mediaInputRef.current.value = "";
      await fetchMedia();
    } catch {
      toast.error("L’upload a échoué. Vérifiez le format ou réessayez.");
    }
  };

  const handleDeleteMedia = async (id) => {
    const API = getApiBase();
    await axios.delete(`${API}/api/admin/bim-3d/media/${id}`, { withCredentials: true });
    toast.success("Média supprimé.");
    await fetchMedia();
  };

  const activeMedia = mediaPreview[activeMediaIndex] || null;

  const handleDeleteCurrentMedia = async () => {
    if (!activeMedia) return;
    const confirmed = window.confirm("Supprimer le média actuellement affiché ?");
    if (!confirmed) return;
    await handleDeleteMedia(activeMedia.id);
    setActiveMediaIndex(0);
  };

  const updateRule = (section, key, value) => {
    setRules((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [key]: section === "generalSettings" && key === "synthesisOptionEnabled" ? value : toNumber(value),
      },
    }));
  };

  const updateRateTier = (index, key, value) => {
    setRules((current) => {
      const next = [...current.rateTiers];
      next[index] = { ...next[index], [key]: toNumber(value) };
      return { ...current, rateTiers: next };
    });
  };

  const validateRules = () => {
    const invalid = [];
    groups.forEach((group) => {
      Object.entries(rules[group.key]).forEach(([key, value]) => {
        if (group.key === "generalSettings" && key === "sharedInfrastructureFactor") return;
        if (group.key === "generalSettings" && key === "synthesisOptionEnabled") return;
        if (value === "" || Number.isNaN(Number(value)) || Number(value) < 0) invalid.push(`${group.title} / ${key}`);
      });
    });
    rules.rateTiers.forEach((tier, index) => {
      if (Number(tier.min) < 0 || Number(tier.rate) < 0) invalid.push(`Palier ${index + 1}`);
    });
    return invalid;
  };

  const example3D = useMemo(() => {
    const surface = 1000;
    const base = Number(rules.generalSettings?.baseJhPerM2 || 0);
    const lod = Number(rules.lodFactors?.["300"] || 1);
    const loi = Number(rules.loiFactors?.["LOI Détaillé"] || 1);
    const disciplineSum = Number(rules.disciplineCoefficients?.Architecture || 0)
      + Number(rules.disciplineCoefficients?.["Structure (Coffrage)"] || 0)
      + Number(rules.disciplineCoefficients?.CVC || 0);
    const modelingDays = surface * base * lod * loi * disciplineSum;
    const totalDays = modelingDays;
    const applicableRate = getTierRate(totalDays, rules.rateTiers);
    return {
      surface,
      base,
      lod,
      loi,
      disciplineSum,
      modelingDays,
      totalDays,
      applicableRate,
      totalAmount: totalDays * applicableRate,
    };
  }, [rules]);

  const handleSaveRules = async () => {
    const invalid = validateRules();
    if (invalid.length) {
      toast.error(`Valeurs invalides : ${invalid[0]}`);
      return;
    }
    const API = getApiBase();
    await axios.put(`${API}/api/admin/bim-3d/pricing-rules`, rules, { withCredentials: true });
    toast.success("Règles de calcul sauvegardées.");
    await fetchRules();
  };

  const handleResetRules = async () => {
    const API = getApiBase();
    await axios.post(`${API}/api/admin/bim-3d/pricing-rules/reset`, {}, { withCredentials: true });
    toast.success("Règles réinitialisées aux valeurs par défaut.");
    await fetchRules();
  };

  return (
    <TooltipProvider>
    <AdminLayout description="Gérez les médias du manège public BIM 3D + Synthèse et les règles de calcul qui alimentent l’estimation visible côté visiteur." title="BIM 3D + Synthèse">
      <Tabs className="space-y-6" data-testid="admin-bim3d-tabs" defaultValue="media">
        <TabsList className="grid w-full max-w-xl grid-cols-2 bg-white/88" data-testid="admin-bim3d-tabs-list">
          <TabsTrigger data-testid="admin-bim3d-tab-media" value="media">Médias du Hero</TabsTrigger>
          <TabsTrigger data-testid="admin-bim3d-tab-rules" value="rules">Règles de calcul</TabsTrigger>
        </TabsList>

        <TabsContent value="media">
          <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-bim3d-media-simple-card">
            <CardContent className="p-8">
              <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Médias du Hero</div>

              {mediaPreview.length ? (
                <div className="mt-6" data-testid="admin-bim3d-media-carousel-wrapper">
                  <Carousel className="mx-auto max-w-[1100px]" opts={{ loop: mediaPreview.length > 1 }} setApi={setMediaCarouselApi}>
                    <CarouselContent className="-ml-0">
                      {mediaPreview.map((item) => (
                        <CarouselItem className="pl-0" key={item.id}>
                          <div className="relative overflow-hidden rounded-[2rem] border border-[#d9e7d5] bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_22px_70px_-48px_rgba(15,118,110,0.2)]" data-testid={`admin-bim3d-carousel-media-${item.id}`}>
                            <img alt={item.title || item.filename} className="h-[24rem] w-full object-contain bg-[#f8fcf3] lg:h-[32rem]" data-testid={`admin-bim3d-carousel-preview-${item.id}`} src={item.previewUrl} />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>

                  <div className="mt-6 flex items-center justify-between gap-4" data-testid="admin-bim3d-media-carousel-nav">
                    <div className="flex items-center gap-2" data-testid="admin-bim3d-media-carousel-dots">
                      {mediaPreview.map((item, index) => (
                        <button
                          className={`h-2.5 rounded-full transition-all ${activeMediaIndex === index ? "w-10 bg-[#0f766e]" : "w-2.5 bg-[#0f766e]/25 hover:bg-[#0f766e]/45"}`}
                          data-testid={`admin-bim3d-media-dot-${index + 1}`}
                          key={item.id}
                          onClick={() => mediaCarouselApi?.scrollTo(index)}
                          type="button"
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim3d-media-prev-button" onClick={() => mediaCarouselApi?.scrollPrev()} type="button" variant="outline">
                        Précédent
                      </Button>
                      <Button className="rounded-full border-[#d9e7d5] bg-white/80 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim3d-media-next-button" onClick={() => mediaCarouselApi?.scrollNext()} type="button" variant="outline">
                        Suivant
                      </Button>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center justify-center gap-4" data-testid="admin-bim3d-media-actions">
                    <input
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      data-testid="admin-bim3d-media-input"
                      multiple
                      onChange={(e) => handleUpload(Array.from(e.target.files || []))}
                      ref={mediaInputRef}
                      type="file"
                    />
                    <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-bim3d-upload-button" onClick={() => mediaInputRef.current?.click()} type="button">
                      Upload
                    </Button>
                    <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim3d-delete-button" onClick={handleDeleteCurrentMedia} type="button" variant="outline">
                      Delete
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-6 rounded-[1.8rem] border border-dashed border-[#d9e7d5] bg-[#f8fcf3] px-6 py-16 text-center" data-testid="admin-bim3d-media-empty-state">
                  <div className="text-lg text-slate-500">Aucun média uploadé pour le moment</div>
                  <input
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    data-testid="admin-bim3d-media-input"
                    multiple
                    onChange={(e) => handleUpload(Array.from(e.target.files || []))}
                    ref={mediaInputRef}
                    type="file"
                  />
                  <Button className="mt-6 rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-bim3d-upload-button" onClick={() => mediaInputRef.current?.click()} type="button">
                    Upload
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules">
          <Card className="rounded-[2rem] border border-[#d9e7d5]/90 bg-white/92 shadow-[0_24px_70px_-48px_rgba(15,118,110,0.2)]" data-testid="admin-bim3d-rules-card">
            <CardContent className="p-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Règles du calculateur</div>
                  <div className="mt-2 text-sm text-slate-500" data-testid="admin-bim3d-rules-updated-at">Dernière modification : {updatedAt ? new Date(updatedAt).toLocaleString("fr-FR") : "-"}</div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim3d-rules-cancel-button" onClick={() => setRules(savedRules)} type="button" variant="outline">Annuler</Button>
                  <Button className="rounded-full border-[#d8ece4] bg-white/90 text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid="admin-bim3d-rules-reset-button" onClick={handleResetRules} type="button" variant="outline">Réinitialiser les valeurs par défaut</Button>
                  <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid="admin-bim3d-rules-save-button" onClick={handleSaveRules} type="button">Sauvegarder</Button>
                </div>
              </div>

              <div className="mt-8 rounded-[1.8rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96)_0%,_rgba(248,252,243,0.95)_100%)] p-6" data-testid="admin-bim3d-formula-card">
                <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Exemple de calcul BIM 3D</div>
                <div className="mt-5 grid gap-6 xl:grid-cols-[1fr_0.95fr]">
                  <div className="rounded-[1.4rem] border border-[#d9e7d5] bg-white/90 p-5">
                    <h3 className="bim-display text-xl font-semibold text-slate-950">Formule générale simplifiée</h3>
                    <div className="mt-4 space-y-4 text-sm leading-7 text-slate-700">
                      <div><strong>Jours modélisation</strong> = Surface retenue × Base j-h/m² × Facteur LOD × Facteur LOI × Somme coefficients disciplines</div>
                      <div><strong>Jours synthèse</strong> = Jours modélisation si l’option Synthèse BIM est activée</div>
                      <div><strong>Jours plans</strong> = Nombre de plans × Jours par plan</div>
                      <div><strong>Total jours-homme</strong> = (Jours modélisation + Jours synthèse + Jours plans) × Facteur urgence</div>
                      <div><strong>Montant HT</strong> = Total jours-homme × Taux journalier applicable</div>
                    </div>
                  </div>
                  <div className="rounded-[1.4rem] border border-[#d9e7d5] bg-white/90 p-5" data-testid="admin-bim3d-formula-example">
                    <h3 className="bim-display text-xl font-semibold text-slate-950">Exemple chiffré</h3>
                    <div className="mt-4 space-y-2 text-sm text-slate-700">
                      <div>Surface : <strong>{example3D.surface.toLocaleString("fr-FR")} m²</strong></div>
                      <div>Base : <strong>{example3D.base.toLocaleString("fr-FR")} j-h/m²</strong></div>
                      <div>LOD : <strong>300 → facteur {example3D.lod.toLocaleString("fr-FR")}</strong></div>
                      <div>LOI : <strong>Détaillé → facteur {example3D.loi.toLocaleString("fr-FR")}</strong></div>
                      <div>Disciplines : <strong>Architecture + Structure + CVC → somme coefficients {example3D.disciplineSum.toLocaleString("fr-FR")}</strong></div>
                      <div>Synthèse : <strong>non</strong></div>
                      <div>Plans : <strong>0</strong></div>
                      <div>Urgence : <strong>standard → facteur 1,00</strong></div>
                    </div>
                    <div className="mt-5 space-y-3 rounded-[1.2rem] bg-[#f8fcf3] p-4 text-sm leading-7 text-slate-700">
                      <div><strong>Jours modélisation</strong> = {example3D.surface} × {example3D.base} × {example3D.lod} × {example3D.loi} × {example3D.disciplineSum.toLocaleString("fr-FR")} = <strong>{example3D.modelingDays.toFixed(2)} j-h</strong></div>
                      <div><strong>Total jours-homme</strong> = <strong>{example3D.totalDays.toFixed(2)} j-h</strong></div>
                      <div><strong>Taux applicable</strong> = <strong>{example3D.applicableRate.toLocaleString("fr-FR")} € / j-h</strong></div>
                      <div><strong>Montant HT</strong> = {example3D.totalDays.toFixed(2)} × {example3D.applicableRate} = <strong>{Math.round(example3D.totalAmount).toLocaleString("fr-FR")} € HT</strong></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 grid gap-6 xl:grid-cols-2">
                {groups.map((group) => (
                  <Card className="rounded-[1.7rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_20px_60px_-46px_rgba(15,118,110,0.18)]" data-testid={`admin-bim3d-rules-group-${group.key}`} key={group.key}>
                    <CardContent className="p-6">
                      <h3 className="bim-display text-xl font-semibold text-slate-950">{group.title}</h3>
                      <div className="mt-5 grid gap-4 md:grid-cols-2">
                        {Object.entries(rules[group.key] || {}).filter(([key]) => !(group.key === "generalSettings" && key === "sharedInfrastructureFactor")).map(([key, value]) => (
                          <div key={key}>
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`${group.key}-${key}`}>{key}</Label>
                              {group.key === "generalSettings" && key === "synthesisOptionEnabled" ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button className="inline-flex text-[#0f766e]" data-testid="admin-bim3d-synthesis-help-button" type="button">
                                      <HelpCircle className="h-4 w-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-sm text-sm" data-testid="admin-bim3d-synthesis-help-tooltip">
                                    La synthèse BIM n’a pas de coefficient dédié ici. Si l’option est activée côté public et cochée dans le calculateur, elle ajoute exactement 100% des jours de modélisation 3D. Si elle est désactivée ici, cette option n’est plus disponible côté public.
                                  </TooltipContent>
                                </Tooltip>
                              ) : null}
                            </div>
                            {group.key === "generalSettings" && key === "synthesisOptionEnabled" ? (
                              <div className="mt-3 flex items-center justify-between rounded-xl border border-[#d9e7d5] bg-white/80 px-4 py-3">
                                <span className="text-sm text-slate-600">Activer l’option synthèse BIM</span>
                                <Switch checked={!!value} data-testid={`admin-bim3d-rule-${group.key}-${key}`} onCheckedChange={(checked) => updateRule(group.key, key, checked)} />
                              </div>
                            ) : (
                              <Input data-testid={`admin-bim3d-rule-${group.key}-${key}`} id={`${group.key}-${key}`} min={0} onChange={(e) => updateRule(group.key, key, e.target.value)} type="number" value={value} />
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Card className="rounded-[1.7rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.92)_0%,_rgba(240,253,244,0.95)_100%)] shadow-[0_20px_60px_-46px_rgba(15,118,110,0.18)] xl:col-span-2" data-testid="admin-bim3d-rate-tiers-card">
                  <CardContent className="p-6">
                    <h3 className="bim-display text-xl font-semibold text-slate-950">Paliers de taux journalier</h3>
                    <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {rules.rateTiers.map((tier, index) => (
                        <div className="rounded-2xl border border-[#d9e7d5] bg-white/80 p-4" data-testid={`admin-bim3d-rate-tier-${index + 1}`} key={`${tier.min}-${index}`}>
                          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0f766e]">Palier {index + 1}</div>
                          <div className="mt-3 grid gap-3">
                            <div>
                              <Label htmlFor={`rate-tier-min-${index}`}>Seuil jours-homme</Label>
                              <Input data-testid={`admin-bim3d-rate-tier-min-${index + 1}`} id={`rate-tier-min-${index}`} min={0} onChange={(e) => updateRateTier(index, "min", e.target.value)} type="number" value={tier.min} />
                            </div>
                            <div>
                              <Label htmlFor={`rate-tier-rate-${index}`}>Taux journalier</Label>
                              <Input data-testid={`admin-bim3d-rate-tier-rate-${index + 1}`} id={`rate-tier-rate-${index}`} min={0} onChange={(e) => updateRateTier(index, "rate", e.target.value)} type="number" value={tier.rate} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
    </TooltipProvider>
  );
}